import React, { useEffect, useState } from 'react';
import axios from 'axios';
import InputField from 'components/fields/InputField';
import {
  NewFeeGroupData,
  FeeDataItem,
  FIXED_FEE_TYPES,
  getFeeTypeDisplayName,
  generateDecimalPlaceholder,
  filterNumericInput,
} from '../types';

interface FeeGroupFormProps {
  value: NewFeeGroupData;
  onChange: (value: NewFeeGroupData) => void;
  disabled?: boolean;
}

const FeeGroupForm: React.FC<FeeGroupFormProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    FIXED_FEE_TYPES.reduce((acc, type, index) => ({ ...acc, [type]: index === 0 }), {})
  );

  const areAllOpen = Object.values(openSections).every(Boolean);

  const toggleAllSections = () => {
    const newState = !areAllOpen;
    setOpenSections(
      Object.keys(openSections).reduce((acc, key) => ({ ...acc, [key]: newState }), {})
    );
  };

  useEffect(() => {
    if (value.fee_data.length === 0) {
      fetchAssetWhitelist();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchAssetWhitelist = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/feegroup/all-assets`
      );

      const assetList = response.data?.assets?.payload || [];

      // Initialize fee data for all combinations of assets and types
      const data: FeeDataItem[] = [];
      FIXED_FEE_TYPES.forEach((type) => {
        assetList.forEach((a: any) => {
          const assetDecimal = a.decimal ?? 2;
          const baseDisplayName = a.display_name || a.code || a.asset;
          if (a.crypto === false) {
            data.push({
              type,
              asset: a.code || a.asset,
              asset_type: "fiat",
              display_name: baseDisplayName,
              decimal: assetDecimal,
              fixed: generateDecimalPlaceholder(assetDecimal),
              percentage: "0",
              max_amount: generateDecimalPlaceholder(assetDecimal),
              min_amount: generateDecimalPlaceholder(assetDecimal),
              is_crypto: false,
            });
          } else {
            if (a.networks && a.networks.length !== 0) {
              const hasMultipleNetworks = a.networks.length > 1;
              a.networks.forEach((n: any) => {
                const cryptoDisplayName = hasMultipleNetworks
                  ? `${baseDisplayName}-${n.display_name || n.network}`
                  : baseDisplayName;
                data.push({
                  type,
                  asset: a.code || a.asset,
                  asset_type: n.network.toLowerCase(),
                  display_name: cryptoDisplayName,
                  decimal: assetDecimal,
                  fixed: generateDecimalPlaceholder(assetDecimal),
                  percentage: "0",
                  max_amount: generateDecimalPlaceholder(assetDecimal),
                  min_amount: generateDecimalPlaceholder(assetDecimal),
                  is_crypto: true,
                });
              });
            }
          }
        });
      });

      onChange({ ...value, fee_data: data });
    } catch (error) {
      console.error('Error fetching asset whitelist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeeDataChange = (index: number, field: string, fieldValue: string) => {
    const newFeeData = value.fee_data.map((item, i) =>
      i === index ? { ...item, [field]: fieldValue } : item
    );
    onChange({ ...value, fee_data: newFeeData });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="h-16 animate-pulse rounded-md bg-gray-200" style={{ animationDuration: '0.6s' }} />
          <div className="h-16 animate-pulse rounded-md bg-gray-200" style={{ animationDuration: '0.6s' }} />
        </div>
        <div className="h-32 animate-pulse rounded-md bg-gray-200" style={{ animationDuration: '0.6s' }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Name and Description */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <InputField
            id="feeGroupName"
            label="Name"
            placeholder="Enter fee group name"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            disabled={disabled}
            variant="auth"
            extra="w-full"
            type="text"
            required
          />
        </div>
        <div>
          <InputField
            id="feeGroupDescription"
            label="Description"
            placeholder="Enter description"
            value={value.description}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            disabled={disabled}
            variant="auth"
            extra="w-full"
            type="text"
            required
          />
        </div>
      </div>

      {/* Fee Configuration */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-navy-700">Fee Configuration</h4>
          <button
            type="button"
            onClick={toggleAllSections}
            disabled={disabled}
            className="text-xs font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
          >
            {areAllOpen ? 'Collapse All' : 'Show All'}
          </button>
        </div>

        {FIXED_FEE_TYPES.map((type) => {
          const typeFees = value.fee_data.filter((f) => f.type === type);
          const fiatFees = typeFees.filter((f) => !f.is_crypto);
          const cryptoFees = typeFees.filter((f) => f.is_crypto);

          return (
            <div key={type} className="mb-3 rounded-lg border border-gray-200 bg-white">
              <button
                type="button"
                className="flex w-full items-center justify-between p-3 text-sm font-semibold capitalize text-navy-700"
                onClick={() => setOpenSections((prev) => ({ ...prev, [type]: !prev[type] }))}
                disabled={disabled}
              >
                <span>{getFeeTypeDisplayName(type)}</span>
                <svg
                  className={`h-4 w-4 transform transition-transform duration-200 ${openSections[type] ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {openSections[type] && (
                <div className="border-t border-gray-200 p-3">
                  {/* Fiat Section */}
                  {fiatFees.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-2 text-xs font-semibold text-gray-600">Fiat</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '25%' }}>Asset</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '18.75%' }}>Min</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '18.75%' }}>Max</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '18.75%' }}>Percentage</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '18.75%' }}>Fixed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fiatFees.map((fee) => {
                              const globalIndex = value.fee_data.indexOf(fee);
                              return (
                                <tr key={globalIndex} className="border-b border-gray-100">
                                  <td className="px-2 py-1.5 text-gray-900">{fee.display_name}</td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.min_amount}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'min_amount', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder={generateDecimalPlaceholder(fee.decimal)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.max_amount}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'max_amount', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder={generateDecimalPlaceholder(fee.decimal)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.percentage}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'percentage', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder="0"
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.fixed}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'fixed', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder={generateDecimalPlaceholder(fee.decimal)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Crypto Section */}
                  {cryptoFees.length > 0 && (
                    <div>
                      <h5 className="mb-2 text-xs font-semibold text-gray-600">Crypto</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '20%' }}>Asset</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '15%' }}>Network</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '16.25%' }}>Min</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '16.25%' }}>Max</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '16.25%' }}>Percentage</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500" style={{ width: '16.25%' }}>Fixed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cryptoFees.map((fee) => {
                              const globalIndex = value.fee_data.indexOf(fee);
                              return (
                                <tr key={globalIndex} className="border-b border-gray-100">
                                  <td className="px-2 py-1.5 text-gray-900">{fee.display_name}</td>
                                  <td className="px-2 py-1.5 text-gray-500">{fee.asset_type}</td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.min_amount}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'min_amount', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder={generateDecimalPlaceholder(fee.decimal)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.max_amount}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'max_amount', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder={generateDecimalPlaceholder(fee.decimal)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.percentage}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'percentage', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder="0"
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={fee.fixed}
                                      onChange={(e) => handleFeeDataChange(globalIndex, 'fixed', filterNumericInput(e.target.value))}
                                      disabled={disabled}
                                      placeholder={generateDecimalPlaceholder(fee.decimal)}
                                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary-500"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeeGroupForm;
